import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Trophy, Sparkles, Clock, Ticket } from 'lucide-react';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
`;

const glow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 20px rgba(251,191,36,0.4)); }
  50% { filter: drop-shadow(0 0 40px rgba(251,191,36,0.6)); }
`;

const HeroContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 2rem 1.5rem;
  margin-bottom: 1.5rem;
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 24px;
  overflow: hidden;
  
  /* Gradient border effect */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    border-radius: 24px;
    background: ${({ theme }) => theme.gradients.cardBorder};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  /* Background mesh gradient */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ theme }) => theme.gradients.mesh};
    pointer-events: none;
    opacity: 0.5;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    text-align: center;

    > * {
      width: 100%;
    }

    > *:not(:last-child) {
      padding-bottom: 1.25rem;
      margin-bottom: 0.25rem;
      border-bottom: 1px solid ${({ theme }) => theme.colors.glassBorder};
    }
  }
`;

const StatGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    align-items: center;
  }
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1.25rem;
  width: fit-content;
  max-width: 320px;
  background: ${({ theme }) => theme.colors.glass};
  border: 1px solid ${({ theme }) => theme.colors.glassBorder};
  border-radius: 16px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.glassHover};
    border-color: rgba(0, 208, 192, 0.2);
  }
`;

const StatIcon = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.$color || 'rgba(0, 208, 192, 0.1)'};
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.secondary};
`;

const JackpotCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const JackpotLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gold};
  text-transform: uppercase;
  letter-spacing: 2px;

  svg {
    animation: ${float} 3s ease-in-out infinite;
  }
`;

const JackpotAmount = styled.div`
  font-size: 3.5rem;
  font-weight: 800;
  font-family: ${({ theme }) => theme.fonts.primary};
  background: ${({ theme }) => theme.gradients.gold};
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 3s linear infinite, ${glow} 2s ease-in-out infinite;
  text-shadow: 0 0 40px rgba(251, 191, 36, 0.3);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const JackpotSubtext = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    background: rgba(34, 197, 94, 0.1);
    border: 1px solid rgba(34, 197, 94, 0.2);
    border-radius: 6px;
    color: ${({ theme }) => theme.colors.success};
    font-size: 0.75rem;
    font-weight: 600;
  }
`;

const CountdownSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const CountdownLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CountdownTimer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TimeUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimeValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.secondary};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.glass};
  border: 1px solid ${({ theme }) => theme.colors.glassBorder};
  border-radius: 12px;
  padding: 0.5rem 0.75rem;
  min-width: 60px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    min-width: 48px;
    padding: 0.375rem 0.5rem;
  }
`;

const TimeLabel = styled.span`
  font-size: 0.625rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  margin-top: 0.25rem;
`;

const TimeSeparator = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  align-self: center;
  margin-bottom: 1rem;
  animation: ${pulse} 1s ease-in-out infinite;
`;

interface HeroJackpotProps {
  totalPool: string;
  timeRemaining: number;
  ticketsSold?: number;
  lotteryId?: number;
}

export const HeroJackpot: React.FC<HeroJackpotProps> = ({
  totalPool,
  timeRemaining,
  ticketsSold = 0,
  lotteryId = 1,
}) => {
  const hours = Math.floor(timeRemaining / 3600);
  const minutes = Math.floor((timeRemaining % 3600) / 60);
  const seconds = timeRemaining % 60;

  const formatTime = (val: number) => val.toString().padStart(2, '0');

  const parseEthFromFormatted = (formatted: string): number | null => {
    const match = formatted.match(/([0-9]+(?:\.[0-9]+)?)/);
    if (!match) return null;
    const n = Number(match[1]);
    return Number.isFinite(n) ? n : null;
  };

  const formatEthAmount = (amount: number): string => {
    if (!Number.isFinite(amount)) return '0.0000 ETH';
    return `${amount.toFixed(4)} ETH`;
  };

  const totalPoolEth = parseEthFromFormatted(totalPool);
  const winnerJackpotEth = totalPoolEth === null ? null : totalPoolEth * 0.8;
  const winnerJackpotDisplay =
    winnerJackpotEth === null ? totalPool : formatEthAmount(winnerJackpotEth);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[HeroJackpot] jackpot winner calc', {
        totalPool,
        totalPoolEth,
        winnerJackpotEth,
        winnerJackpotDisplay,
      });
    }
  }, [totalPool, totalPoolEth, winnerJackpotEth, winnerJackpotDisplay]);

  return (
    <HeroContainer>
      <HeroContent>
        <StatGroup>
          <StatCard>
            <StatIcon $color="rgba(99, 102, 241, 0.1)">
              <Ticket />
            </StatIcon>
            <StatContent>
              <StatLabel>Round #{lotteryId} : Tickets Sold</StatLabel>
              <StatValue>{ticketsSold} tickets</StatValue>
            </StatContent>
          </StatCard>
        </StatGroup>

        <JackpotCenter>
          <JackpotLabel>
            <Trophy size={18} />
            Current Jackpot
            <Trophy size={18} />
          </JackpotLabel>
          <JackpotAmount>{winnerJackpotDisplay}</JackpotAmount>
          <JackpotSubtext>
            <span>
              <Sparkles size={12} />
              LIVE
            </span>
            Growing with every ticket
          </JackpotSubtext>
        </JackpotCenter>

        <CountdownSection>
          <CountdownLabel>
            <Clock size={14} />
            Drawing In
          </CountdownLabel>
          <CountdownTimer>
            <TimeUnit>
              <TimeValue>{formatTime(hours)}</TimeValue>
              <TimeLabel>Hours</TimeLabel>
            </TimeUnit>
            <TimeSeparator>:</TimeSeparator>
            <TimeUnit>
              <TimeValue>{formatTime(minutes)}</TimeValue>
              <TimeLabel>Mins</TimeLabel>
            </TimeUnit>
            <TimeSeparator>:</TimeSeparator>
            <TimeUnit>
              <TimeValue>{formatTime(seconds)}</TimeValue>
              <TimeLabel>Secs</TimeLabel>
            </TimeUnit>
          </CountdownTimer>
        </CountdownSection>
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroJackpot;
